import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, TrendingUp, History } from 'lucide-react';

const API = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ? 'http://localhost:8001/api'
  : '/api';

function ManipulatePanel() {
  const [users, setUsers] = useState([]);
  const [assets, setAssets] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [transactionType, setTransactionType] = useState('open'); // 'open' or 'completed'
  
  const [formData, setFormData] = useState({
    user_id: '',
    asset_id: '',
    symbol: '',
    amount: 0,
    entry_price: 0,
    exit_price: 0,
    entry_date: new Date().toISOString().slice(0, 16),
    exit_date: new Date().toISOString().slice(0, 16),
    stop_loss: null,
    take_profit: null
  });

  const token = localStorage.getItem('token');
  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  useEffect(() => {
    fetchUsers();
    fetchAssets();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, axiosConfig);
      setUsers(res.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchAssets = async () => {
    try {
      const res = await axios.get(`${API}/assets`, axiosConfig);
      setAssets(res.data);
    } catch (error) {
      console.error('Error fetching assets:', error);
    }
  };

  const handleAssetChange = (assetId) => {
    const asset = assets.find(a => a.id === assetId);
    if (asset) {
      setFormData({
        ...formData,
        asset_id: assetId,
        symbol: asset.symbol,
        entry_price: asset.current_price
      });
    }
  };

  const calculateProfitLoss = () => {
    const pnl = (formData.exit_price - formData.entry_price) * formData.amount;
    const pnl_percent = ((formData.exit_price - formData.entry_price) / formData.entry_price) * 100;
    return { pnl, pnl_percent };
  };

  const handleCreateOpenPosition = async () => {
    try {
      await axios.post(
        `${API}/admin/positions/create`,
        {
          user_id: formData.user_id,
          asset_id: formData.asset_id,
          asset_symbol: formData.symbol,
          amount: parseFloat(formData.amount),
          entry_price: parseFloat(formData.entry_price),
          avg_buy_price: parseFloat(formData.entry_price),
          entry_date: formData.entry_date,
          stop_loss: formData.stop_loss ? parseFloat(formData.stop_loss) : null,
          take_profit: formData.take_profit ? parseFloat(formData.take_profit) : null
        },
        axiosConfig
      );
      
      alert('✅ Açık pozisyon oluşturuldu!');
      setShowModal(false);
      resetForm();
    } catch (error) {
      alert('❌ ' + (error.response?.data?.detail || 'Hata oluştu!'));
    }
  };

  const handleCreateCompletedTransaction = async () => {
    try {
      const { pnl } = calculateProfitLoss();
      
      await axios.post(
        `${API}/admin/transactions/create`,
        {
          user_id: formData.user_id,
          asset_symbol: formData.symbol,
          amount: parseFloat(formData.amount),
          entry_price: parseFloat(formData.entry_price),
          exit_price: parseFloat(formData.exit_price),
          profit_loss: pnl,
          entry_date: formData.entry_date,
          exit_date: formData.exit_date
        },
        axiosConfig
      );
      
      alert('✅ Tamamlanmış işlem oluşturuldu!');
      setShowModal(false);
      resetForm();
    } catch (error) {
      alert('❌ ' + (error.response?.data?.detail || 'Hata oluştu!'));
    }
  };

  const resetForm = () => {
    setFormData({
      user_id: '',
      asset_id: '',
      symbol: '',
      amount: 0,
      entry_price: 0,
      exit_price: 0,
      entry_date: new Date().toISOString().slice(0, 16),
      exit_date: new Date().toISOString().slice(0, 16),
      stop_loss: null,
      take_profit: null
    });
  };

  const { pnl, pnl_percent } = transactionType === 'completed' ? calculateProfitLoss() : { pnl: 0, pnl_percent: 0 };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-white font-bold text-3xl mb-2">İşlem Manipülasyonu</h2>
        <p className="text-gray-400">Kullanıcılara geçmişe dönük işlem veya açık pozisyon ekleyin</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <button
          onClick={() => {
            setTransactionType('open');
            setShowModal(true);
          }}
          className="bg-gradient-to-br from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white p-8 rounded-xl transition-all flex items-center gap-4 shadow-lg"
        >
          <TrendingUp className="w-12 h-12" />
          <div className="text-left">
            <h3 className="text-xl font-bold">Açık Pozisyon Ekle</h3>
            <p className="text-blue-200 text-sm mt-1">Aktif işlem olarak görünür</p>
          </div>
        </button>

        <button
          onClick={() => {
            setTransactionType('completed');
            setShowModal(true);
          }}
          className="bg-gradient-to-br from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white p-8 rounded-xl transition-all flex items-center gap-4 shadow-lg"
        >
          <History className="w-12 h-12" />
          <div className="text-left">
            <h3 className="text-xl font-bold">Tamamlanmış İşlem Ekle</h3>
            <p className="text-green-200 text-sm mt-1">Geçmiş işlemlere eklenir</p>
          </div>
        </button>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gray-800 rounded-xl p-8 w-full max-w-2xl border border-gray-700 my-8">
            <h2 className="text-white font-bold text-2xl mb-6">
              {transactionType === 'open' ? '📈 Açık Pozisyon Ekle' : '📋 Tamamlanmış İşlem Ekle'}
            </h2>
            
            <div className="space-y-4">
              {/* User Selection */}
              <div>
                <label className="block text-gray-300 mb-2">Kullanıcı</label>
                <select
                  value={formData.user_id}
                  onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                  className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg outline-none"
                >
                  <option value="">Kullanıcı Seçin</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.full_name || user.email} ({user.email})
                    </option>
                  ))}
                </select>
              </div>

              {/* Asset Selection */}
              <div>
                <label className="block text-gray-300 mb-2">Varlık</label>
                <select
                  value={formData.asset_id}
                  onChange={(e) => handleAssetChange(e.target.value)}
                  className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg outline-none"
                >
                  <option value="">Varlık Seçin</option>
                  
                  {/* BIST 100 */}
                  {assets.filter(a => a.category === 'BIST_100').length > 0 && (
                    <optgroup label="📊 BIST 100">
                      {assets.filter(a => a.category === 'BIST_100').map(asset => (
                        <option key={asset.id} value={asset.id}>
                          {asset.symbol} - ₺{asset.current_price.toFixed(2)}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  
                  {/* VIOP */}
                  {assets.filter(a => a.category === 'VIOP').length > 0 && (
                    <optgroup label="📈 VİOP (Vadeli İşlem)">
                      {assets.filter(a => a.category === 'VIOP').map(asset => (
                        <option key={asset.id} value={asset.id}>
                          {asset.symbol} - ₺{asset.current_price.toFixed(2)}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  
                  {/* Uluslararası Hisseler */}
                  {assets.filter(a => a.category === 'HISSELER').length > 0 && (
                    <optgroup label="🌍 Uluslararası Hisseler">
                      {assets.filter(a => a.category === 'HISSELER').map(asset => (
                        <option key={asset.id} value={asset.id}>
                          {asset.symbol} - ₺{asset.current_price.toFixed(2)}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  
                  {/* Endeksler */}
                  {assets.filter(a => a.category === 'ENDEKSLER').length > 0 && (
                    <optgroup label="📊 Endeksler">
                      {assets.filter(a => a.category === 'ENDEKSLER').map(asset => (
                        <option key={asset.id} value={asset.id}>
                          {asset.symbol} - ₺{asset.current_price.toFixed(2)}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  
                  {/* Emtia */}
                  {assets.filter(a => a.category === 'EMTIA').length > 0 && (
                    <optgroup label="🪙 Emtia">
                      {assets.filter(a => a.category === 'EMTIA').map(asset => (
                        <option key={asset.id} value={asset.id}>
                          {asset.symbol} - ₺{asset.current_price.toFixed(2)}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  
                  {/* Döviz */}
                  {assets.filter(a => a.category === 'DOVIZ').length > 0 && (
                    <optgroup label="💱 Döviz">
                      {assets.filter(a => a.category === 'DOVIZ').map(asset => (
                        <option key={asset.id} value={asset.id}>
                          {asset.symbol} - ₺{asset.current_price.toFixed(2)}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  
                  {/* Kripto */}
                  {assets.filter(a => a.category === 'KRIPTO').length > 0 && (
                    <optgroup label="₿ Kripto">
                      {assets.filter(a => a.category === 'KRIPTO').map(asset => (
                        <option key={asset.id} value={asset.id}>
                          {asset.symbol} - ₺{asset.current_price.toFixed(2)}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Amount */}
                <div>
                  <label className="block text-gray-300 mb-2">Miktar</label>
                  <input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg outline-none"
                    placeholder="1000"
                  />
                </div>

                {/* Entry Price */}
                <div>
                  <label className="block text-gray-300 mb-2">Giriş Fiyatı (₺)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.entry_price}
                    onChange={(e) => setFormData({ ...formData, entry_price: e.target.value })}
                    className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg outline-none"
                    placeholder="100.00"
                  />
                </div>
              </div>

              {/* Entry Date */}
              <div>
                <label className="block text-gray-300 mb-2">Giriş Tarihi & Saati</label>
                <input
                  type="datetime-local"
                  value={formData.entry_date}
                  onChange={(e) => setFormData({ ...formData, entry_date: e.target.value })}
                  className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg outline-none"
                />
              </div>

              {transactionType === 'completed' ? (
                <>
                  {/* Exit Price */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-300 mb-2">Çıkış Fiyatı (₺)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.exit_price}
                        onChange={(e) => setFormData({ ...formData, exit_price: e.target.value })}
                        className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg outline-none"
                        placeholder="120.00"
                      />
                    </div>

                    {/* Exit Date */}
                    <div>
                      <label className="block text-gray-300 mb-2">Çıkış Tarihi & Saati</label>
                      <input
                        type="datetime-local"
                        value={formData.exit_date}
                        onChange={(e) => setFormData({ ...formData, exit_date: e.target.value })}
                        className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg outline-none"
                      />
                    </div>
                  </div>

                  {/* Profit/Loss Preview */}
                  {formData.entry_price && formData.exit_price && formData.amount && (
                    <div className="bg-gray-700 rounded-lg p-4">
                      <p className="text-gray-400 text-sm mb-2">Kar/Zarar Önizleme:</p>
                      <div className="flex items-center gap-2">
                        <span className={`text-2xl font-bold ${pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {pnl >= 0 ? '+' : ''}₺{pnl.toFixed(2)}
                        </span>
                        <span className={`text-lg ${pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          ({pnl_percent >= 0 ? '+' : ''}{pnl_percent.toFixed(2)}%)
                        </span>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Stop Loss & Take Profit */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-300 mb-2">Stop Loss (Opsiyonel)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.stop_loss || ''}
                        onChange={(e) => setFormData({ ...formData, stop_loss: e.target.value })}
                        className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg outline-none"
                        placeholder="90.00"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-300 mb-2">Take Profit (Opsiyonel)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.take_profit || ''}
                        onChange={(e) => setFormData({ ...formData, take_profit: e.target.value })}
                        className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg outline-none"
                        placeholder="150.00"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-3 rounded-lg transition-colors"
              >
                İptal
              </button>
              <button
                onClick={transactionType === 'open' ? handleCreateOpenPosition : handleCreateCompletedTransaction}
                disabled={!formData.user_id || !formData.asset_id || !formData.amount || !formData.entry_price}
                className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Oluştur
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManipulatePanel;
